import notification from "antd/es/notification";
import { defineMessages } from "react-intl";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { fetchAllFileTags } from "@app/store/fileTags/actions";
import { getOrganizationId } from "@app/store/iam";

import {
  bulkCreateFileTags,
  bulkDeleteFileTags,
  bulkEditFileTags,
  fetchFileTags,
  initialize,
} from "./actions";
import FileTagsViewState from "./FileTagsViewState";
import { getViewState } from "./selectors";

const messages = defineMessages({
  bulkCreate: {
    id: "settings.fileTags.create.bulk.success",
    defaultMessage: "{total, plural, one {Tag} other {Tags}} added",
    description: "Bulk create tags success message",
  },
  bulkCreateError: {
    id: "settings.fileTags.create.bulk.error",
    defaultMessage: "Error adding {total, plural, one {tag} other {tags}}",
    description: "Bulk create tags error message",
  },
  bulkDelete: {
    id: "settings.fileTags.delete.bulk.success",
    defaultMessage: "{total, plural, one {Tag} other {Tags}} deleted",
    description: "Bulk delete tags success message",
  },
  bulkDeleteError: {
    id: "settings.fileTags.delete.bulk.error",
    defaultMessage: "Error deleting {total, plural, one {tag} other {tags}}",
    description: "File tags delete error message",
  },
  bulkEdit: {
    id: "settings.fileTags.edit.bulk.success",
    defaultMessage: "{total, plural, one {Tag} other {Tags}} edited",
    description: "File tags edit success message",
  },
  bulkEditError: {
    id: "settings.fileTags.edit.bulk.error",
    defaultMessage: "Error editing {total, plural, one {tag} other {tags}}",
    description: "File tags edit error message",
  },
  errorDescription: {
    id: "settings.fileTags.update.generic.error.description",
    defaultMessage: "Please try again or contact support.",
    description: "File Tags generic error description",
  },
});

export const DEFAULT_PAGE_SIZE = 25;

export function* onFetchFileTags({ payload }: ReturnType<typeof fetchFileTags.request>) {
  try {
    const { page, sortFieldName, sortOrder, userIds } = payload;
    const organizationId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<FileTag> = yield callApi("fetchFileTags", organizationId, {
      $filters: {
        $and: userIds.length ? [{ userId: { $in: userIds } }] : [],
      },
      $limit: DEFAULT_PAGE_SIZE,
      $offset: (page - 1) * DEFAULT_PAGE_SIZE,
      $order: `${sortOrder === SortOrder.DESC ? "-" : ""}${sortFieldName}`,
    });
    yield put(fetchFileTags.success({ fileTags: response.data, total: response.total }));
  } catch (error) {
    yield put(fetchFileTags.failure());
    yield put(handleError({ error }));
  }
}

export function* onInitialize() {
  const viewState: FileTagsViewState = yield select(getViewState);

  yield put(fetchFileTags.request(viewState));
}

export function* onBulkDeleteFileTags({
  payload: { callback, ids },
}: ReturnType<typeof bulkDeleteFileTags.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewState: FileTagsViewState = yield select(getViewState);

    yield callApi("bulkDeleteFileTags", orgId, ids);
    if (callback) {
      yield call(callback);
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.bulkDelete, { total: ids.length }),
      });
    }
    yield put(fetchFileTags.request(viewState));
    yield put(bulkDeleteFileTags.success());
  } catch (error) {
    yield put(bulkDeleteFileTags.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.bulkDeleteError, { total: ids.length }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onBulkEditFileTags({
  payload: { callback, fileTags },
}: ReturnType<typeof bulkEditFileTags.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewState: FileTagsViewState = yield select(getViewState);

    yield callApi("bulkEditFileTags", orgId, fileTags);

    if (callback) {
      yield call(callback);
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.bulkEdit, { total: fileTags.length }),
      });
    }

    yield put(fetchFileTags.request(viewState));
    yield put(bulkEditFileTags.success());
  } catch (error) {
    yield put(bulkEditFileTags.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.bulkEditError, { total: fileTags.length }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* onBulkCreateFileTags({
  payload: { callback, fileTags },
}: ReturnType<typeof bulkCreateFileTags.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewState: FileTagsViewState = yield select(getViewState);
    yield callApi("bulkCreateFileTags", orgId, fileTags);

    if (callback) {
      yield call(callback);
    }

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.bulkCreate, { total: fileTags.length }),
      });
    }

    yield put(fetchFileTags.request(viewState));
    // refresh all tags in store
    yield put(fetchAllFileTags.request());
    yield put(bulkCreateFileTags.success());
  } catch (error) {
    yield put(bulkCreateFileTags.failure());

    const intl = i18nService.getIntl();
    if (intl) {
      notification.error({
        description: intl.formatMessage(messages.errorDescription),
        message: intl.formatMessage(messages.bulkCreateError, { total: fileTags.length }),
      });
    }
    yield put(handleError({ error }));
  }
}

export function* fileTagsSaga() {
  yield takeLatest(initialize.request, onInitialize);
  yield takeLatest(fetchFileTags.request, onFetchFileTags);
  yield takeEvery(bulkCreateFileTags.request, onBulkCreateFileTags);
  yield takeEvery(bulkEditFileTags.request, onBulkEditFileTags);
  yield takeEvery(bulkDeleteFileTags.request, onBulkDeleteFileTags);
}
